'use client'
import {
  ChevronDownIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/16/solid'
import { useEffect, useRef, useState } from 'react'
import { cn } from '../../helpers/classnames'
import { JosefCheckbox } from '../checkbox/josef-checkbox'

export interface JosefMultiSelectOption {
  value: string
  label: string
  description?: string
}

export interface JosefMultiSelectProps {
  options: JosefMultiSelectOption[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  searchable?: boolean
  maxHeight?: string
  columns?: number
  onCreateNew?: (newValue: string) => void
  isCreating?: boolean
}

export function JosefMultiSelect({
  options,
  selected,
  onChange,
  placeholder = 'Select options...',
  className = '',
  disabled = false,
  searchable = true,
  maxHeight = '300px',
  columns = 2,
  onCreateNew,
  isCreating = false,
}: JosefMultiSelectProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [newValue, setNewValue] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Natural sort function (handles numeric strings properly)
  const naturalSort = (
    a: JosefMultiSelectOption,
    b: JosefMultiSelectOption
  ) => {
    const aLabel = a.label
    const bLabel = b.label

    // Try to parse as numbers first
    const aNum = parseFloat(aLabel)
    const bNum = parseFloat(bLabel)

    // If both are valid numbers, compare numerically
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return aNum - bNum
    }

    // Otherwise use localeCompare with numeric option for natural sorting
    return aLabel.localeCompare(bLabel, undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  }

  // Filter and sort options based on search term
  const filteredOptions = options
    .filter(
      (option) =>
        option.label.toLowerCase().includes(searchTerm.trim().toLowerCase()) ||
        option.description
          ?.toLowerCase()
          .includes(searchTerm.trim().toLowerCase())
    )
    .sort(naturalSort)

  // Group options by description (area name) if descriptions exist
  const hasGroups = filteredOptions.some((opt) => opt.description)
  const groupedOptions = hasGroups
    ? filteredOptions.reduce<Record<string, JosefMultiSelectOption[]>>(
        (acc, opt) => {
          const group = opt.description || 'Other'
          if (!acc[group]) acc[group] = []
          acc[group].push(opt)
          return acc
        },
        {}
      )
    : null

  // Toggle all options in a group
  const toggleGroup = (groupName: string) => {
    if (!groupedOptions) return
    const groupValues = groupedOptions[groupName].map((opt) => opt.value)
    const allSelected = groupValues.every((v) => selected.includes(v))
    if (allSelected) {
      onChange(selected.filter((v) => !groupValues.includes(v)))
    } else {
      onChange([...new Set([...selected, ...groupValues])])
    }
  }

  // Handle option toggle
  const toggleOption = (value: string) => {
    const newSelected = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value]
    onChange(newSelected)
  }

  // Open/close dialog
  const openDialog = () => {
    if (disabled) return
    setIsOpen(true)
    dialogRef.current?.showModal()
    setTimeout(() => searchInputRef.current?.focus(), 100)
  }

  const closeDialog = () => {
    setIsOpen(false)
    setSearchTerm('')
    setNewValue('')
    dialogRef.current?.close()
  }

  // Handle escape key and backdrop click
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    const handleClose = () => {
      closeDialog()
    }

    const handleBackdropClick = (e: MouseEvent) => {
      if (e.target === dialog) {
        closeDialog()
      }
    }

    dialog.addEventListener('close', handleClose)
    dialog.addEventListener('click', handleBackdropClick)

    return () => {
      dialog.removeEventListener('close', handleClose)
      dialog.removeEventListener('click', handleBackdropClick)
    }
  }, [])

  // Clear all selections
  const clearAll = () => {
    onChange([])
  }

  // Select all filtered options
  const selectAllFiltered = () => {
    const allFilteredValues = filteredOptions.map((opt) => opt.value)
    const newSelected = [...new Set([...selected, ...allFilteredValues])]
    onChange(newSelected)
  }

  // Handle create new tag
  const handleCreateNew = () => {
    if (onCreateNew) {
      const valueToCreate = newValue.trim() || searchTerm.trim()
      if (valueToCreate) {
        onCreateNew(valueToCreate)
        setNewValue('')
      }
    }
  }

  // Get display text for trigger
  const getDisplayText = () => {
    if (selected.length === 0) return placeholder
    if (selected.length === 1) {
      const option = options.find((opt) => opt.value === selected[0])
      return option?.label || selected[0]
    }
    return `${selected.length} selected`
  }

  return (
    <div className="space-y-2">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={openDialog}
        disabled={disabled}
        className={cn(
          'relative w-full min-h-[40px] px-3 py-2 text-left text-sm bg-white dark:bg-black rounded-lg text-black dark:text-white transition-colors',
          'focus:outline-none',
          'hover:bg-black/5 dark:hover:bg-white/5',
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
      >
        <div className="flex items-center justify-between">
          <span
            className={cn(
              'block truncate',
              selected.length === 0 && 'text-gray-500 dark:text-gray-400'
            )}
          >
            {getDisplayText()}
          </span>
          <div className="flex items-center gap-2">
            {selected.length > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-black/10 dark:bg-white/10 text-black dark:text-white">
                {selected.length}
              </span>
            )}
            <ChevronDownIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </div>
        </div>
      </button>

      {/* Selected Options Display */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected
            .map((value) => {
              const option = options.find((opt) => opt.value === value)
              if (!option) return null
              return { value, option }
            })
            .filter((item) => item !== null)
            .sort((a, b) => {
              // Natural sort for the selected items display
              const aLabel = a!.option.label
              const bLabel = b!.option.label

              // Try to parse as numbers first
              const aNum = parseFloat(aLabel)
              const bNum = parseFloat(bLabel)

              // If both are valid numbers, compare numerically
              if (!isNaN(aNum) && !isNaN(bNum)) {
                return aNum - bNum
              }

              // Otherwise use localeCompare with numeric option
              return aLabel.localeCompare(bLabel, undefined, {
                numeric: true,
                sensitivity: 'base',
              })
            })
            .map(({ value, option }) => (
              <div
                key={value}
                className="inline-flex items-center gap-1 px-2 py-1 bg-black/5 dark:bg-white/5 rounded text-xs text-black dark:text-white"
              >
                <span className="truncate max-w-[120px]">{option.label}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    toggleOption(value)
                  }}
                  className="flex-shrink-0 hover:bg-black/10 dark:hover:bg-white/10 rounded p-0.5 transition-colors"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
        </div>
      )}

      {/* Native Dialog */}
      <dialog
        ref={dialogRef}
        className={cn(
          'p-0 m-0 border-0 rounded-lg shadow-2xl backdrop:bg-black/50 backdrop:backdrop-blur-sm',
          'bg-[#FAFAFA] dark:bg-[#171717] border border-black/10 dark:border-white/10',
          'w-full max-w-md'
        )}
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          position: 'fixed',
        }}
      >
        <div className="flex flex-col max-h-[80vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-black/10 dark:border-white/10">
            <h3 className="text-sm font-medium text-black dark:text-white">
              Select Options
            </h3>
            <button
              type="button"
              onClick={closeDialog}
              className={cn(
                'inline-flex items-center justify-center w-8 h-8 rounded-lg',
                'text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white',
                'hover:bg-black/5 dark:hover:bg-white/5 transition-colors'
              )}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Search */}
          {searchable && (
            <div className="p-4 border-b border-black/10 dark:border-white/10">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-3 h-4 w-4 text-gray-500 dark:text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={onCreateNew ? "Search or create new..." : "Search options..."}
                  className={cn(
                    'w-full h-10 pl-10 pr-3 py-2 text-sm bg-white dark:bg-black/50',
                    'border border-black/10 dark:border-white/10 rounded-lg',
                    'text-black dark:text-white placeholder-gray-500 dark:placeholder-gray-400',
                    'focus:outline-none focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20',
                    'focus:border-black/20 dark:focus:border-white/20 transition-colors',
                    onCreateNew && 'pr-20'
                  )}
                />
                {onCreateNew && searchTerm.trim() && (
                  <button
                    type="button"
                    onClick={handleCreateNew}
                    disabled={isCreating}
                    className={cn(
                      'absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs font-medium rounded transition-colors',
                      'bg-black dark:bg-white text-white dark:text-black',
                      'hover:bg-black/80 dark:hover:bg-white/80',
                      'disabled:opacity-50 disabled:cursor-not-allowed'
                    )}
                  >
                    {isCreating ? '...' : 'Create'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Bulk Actions */}
          <div className="flex items-center justify-between p-4 border-b border-black/10 dark:border-white/10">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {selected.length} of {options.length} selected
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={selectAllFiltered}
                className={cn(
                  'px-3 py-1 text-xs rounded-lg transition-colors',
                  'text-black dark:text-white hover:bg-black/5 dark:hover:bg-white/5'
                )}
              >
                Select All
              </button>
              <button
                type="button"
                onClick={clearAll}
                className={cn(
                  'px-3 py-1 text-xs rounded-lg transition-colors',
                  'text-black dark:text-white hover:bg-black/5 dark:hover:bg-white/5'
                )}
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Options List */}
          <div className="flex-1 overflow-y-auto" style={{ maxHeight }}>
            {filteredOptions.length === 0 ? (
              <div className="p-4">
                {onCreateNew && searchTerm.trim() ? (
                  <div className="flex flex-col gap-3 items-center">
                    <p className="text-center text-gray-500 dark:text-gray-400 text-sm">
                      Option not found
                    </p>
                    <button
                      type="button"
                      onClick={handleCreateNew}
                      disabled={isCreating}
                      className={cn(
                        'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                        'bg-black dark:bg-white text-white dark:text-black',
                        'hover:bg-black/90 dark:hover:bg-white/90',
                        'disabled:opacity-50 disabled:cursor-not-allowed'
                      )}
                    >
                      {isCreating
                        ? 'Creating...'
                        : `Add "${searchTerm.trim()}"`}
                    </button>
                  </div>
                ) : (
                  <div className="text-center text-gray-500 dark:text-gray-400 text-sm">
                    No options found
                  </div>
                )}
              </div>
            ) : (
              <>
                {onCreateNew &&
                  searchTerm.trim() &&
                  !options.some(
                    (opt) =>
                      opt.value.toLowerCase() === searchTerm.trim().toLowerCase()
                  ) && (
                    <div className="p-2 border-b border-black/5 dark:border-white/5">
                      <button
                        type="button"
                        onClick={handleCreateNew}
                        disabled={isCreating}
                        className={cn(
                          'w-full flex items-center gap-2 p-2 rounded-lg transition-colors text-left',
                          'bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10',
                          'disabled:opacity-50 disabled:cursor-not-allowed'
                        )}
                      >
                        <span className="text-sm font-medium text-black dark:text-white">
                          {isCreating ? 'Creating...' : `Create "${searchTerm.trim()}"`}
                        </span>
                      </button>
                    </div>
                  )}
              <div className="p-2">
                {/* Grouped layout when options have descriptions (area names) */}
                {groupedOptions ? (
                  <div className="space-y-3">
                    {Object.entries(groupedOptions).map(([groupName, groupOpts]) => {
                      const allGroupSelected = groupOpts.every((opt) => selected.includes(opt.value))
                      return (
                        <div key={groupName}>
                          <button
                            type="button"
                            onClick={() => toggleGroup(groupName)}
                            className={cn(
                              'flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-left transition-colors mb-1',
                              'hover:bg-black/5 dark:hover:bg-white/5'
                            )}
                          >
                            <JosefCheckbox
                              checked={allGroupSelected}
                              className="pointer-events-none"
                            />
                            <span className="text-xs font-semibold text-black dark:text-white">
                              {groupName} ({groupOpts.length})
                            </span>
                          </button>
                          <div
                            className={cn(
                              'ml-6 grid gap-1',
                              columns === 1 && 'grid-cols-1',
                              columns === 2 && 'grid-cols-2',
                              columns === 3 && 'grid-cols-3',
                              columns === 4 && 'grid-cols-4',
                              columns === 5 && 'grid-cols-5',
                              columns === 6 && 'grid-cols-6'
                            )}
                          >
                            {groupOpts.map((option) => {
                              const isSelected = selected.includes(option.value)
                              return (
                                <div
                                  key={option.value}
                                  onClick={() => toggleOption(option.value)}
                                  className={cn(
                                    'flex items-center gap-2 p-1.5 rounded-lg transition-colors text-left w-full cursor-pointer',
                                    'hover:bg-black/5 dark:hover:bg-white/5 text-black dark:text-white'
                                  )}
                                >
                                  <JosefCheckbox
                                    checked={isSelected}
                                    onCheckedChange={() => toggleOption(option.value)}
                                    className="pointer-events-none"
                                  />
                                  <div className="text-xs font-medium truncate">
                                    {option.label}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : filteredOptions.length > 6 ? (
                  <div
                    className={cn(
                      'grid gap-2',
                      columns === 1 && 'grid-cols-1',
                      columns === 2 && 'grid-cols-2',
                      columns === 3 && 'grid-cols-3',
                      columns === 4 && 'grid-cols-4',
                      columns === 5 && 'grid-cols-5',
                      columns === 6 && 'grid-cols-6'
                    )}
                  >
                    {filteredOptions.map((option) => {
                      const isSelected = selected.includes(option.value)
                      return (
                        <div
                          key={option.value}
                          onClick={() => toggleOption(option.value)}
                          className={cn(
                            'flex items-center gap-2 p-2 rounded-lg transition-colors text-left w-full cursor-pointer',
                            'hover:bg-black/5 dark:hover:bg-white/5 text-black dark:text-white'
                          )}
                        >
                          <JosefCheckbox
                            checked={isSelected}
                            onCheckedChange={() => toggleOption(option.value)}
                            className="pointer-events-none"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium truncate">
                              {option.label}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {filteredOptions.map((option) => {
                      const isSelected = selected.includes(option.value)
                      return (
                        <div
                          key={option.value}
                          onClick={() => toggleOption(option.value)}
                          className={cn(
                            'w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left cursor-pointer',
                            'hover:bg-black/5 dark:hover:bg-white/5 text-black dark:text-white'
                          )}
                        >
                          <JosefCheckbox
                            checked={isSelected}
                            onCheckedChange={() => toggleOption(option.value)}
                            className="pointer-events-none"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">
                              {option.label}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-black/10 dark:border-white/10">
            <button
              type="button"
              onClick={closeDialog}
              className={cn(
                'w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                'bg-black dark:bg-white text-white dark:text-black',
                'hover:bg-black/90 dark:hover:bg-white/90'
              )}
            >
              Done
            </button>
          </div>
        </div>
      </dialog>
    </div>
  )
}

export default JosefMultiSelect
